"use client";
import Link from "next/link";
import { useApi } from "@/lib/hooks/use-api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/lib/auth/provider";
import { DataState, Empty, PageHeading, Stat } from "@/components/ui";
import { Award, Medal, Target, Trophy } from "lucide-react";

interface BoardItem {
  rank: number;
  id: string;
  name: string;
  /** Только для ADMIN (privacy: публичный рейтинг не раскрывает email). */
  email?: string;
  xp: number;
  practices: number;
  level: number;
}

export function LeaderboardPage() {
  const t = useT();
  const auth = useAuth();
  const board = useApi<{ items: BoardItem[] }>(
    auth.authenticated ? "/leaderboard" : null,
    30_000,
  );
  const top = board.data?.items ?? [];
  const leader = top[0];
  return (
    <div className="page">
      <PageHeading
        eyebrow={t("lb.eyebrow")}
        title={t("lb.title")}
        description={t("lb.desc")}
      />
      {!auth.authenticated ? (
        <Empty
          title={t("lb.guest_title")}
          text={t("lb.guest_text")}
          action={
            <Link className="button primary" href="/login">
              {t("auth.login_btn")}
            </Link>
          }
        />
      ) : (
      <DataState
        loading={board.loading}
        error={board.error}
        retry={board.reload}
      >
        {top.length === 0 ? (
          <Empty title={t("lb.empty")} />
        ) : (
          <>
            <div className="stats-grid" style={{ marginBottom: "18px" }}>
              <Stat
                note={leader ? `${leader.xp} XP` : undefined}
                label={t("lb.leader")}
                value={leader ? leader.name : "—"}
                icon={<Trophy size={16} />}
              />
              <Stat
                label={t("lb.participants")}
                value={String(top.length)}
                icon={<Medal size={16} />}
              />
              <Stat
                label={t("lb.leader_practices")}
                value={leader ? String(leader.practices) : "—"}
                icon={<Target size={16} />}
              />
              <Stat
                label={t("lb.leader_level")}
                value={leader ? `L${leader.level}` : "—"}
                icon={<Award size={16} />}
              />
            </div>
            <div className="card">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t("lb.col_rank")}</th>
                    <th>{t("lb.col_student")}</th>
                    <th>{t("lb.col_level")}</th>
                    <th>{t("lb.col_practices")}</th>
                    <th>XP</th>
                  </tr>
                </thead>
                <tbody>
                  {top.map((r) => (
                    <tr key={r.id}>
                      <td>
                        {r.rank <= 3 ? (
                          <span className={`rank-medal rank-${r.rank}`}>
                            {r.rank === 1 ? (
                              <Trophy size={15} />
                            ) : r.rank === 2 ? (
                              <Medal size={15} />
                            ) : (
                              <Award size={15} />
                            )}
                            {r.rank}
                          </span>
                        ) : (
                          <span className="rank-num">{r.rank}</span>
                        )}
                      </td>
                      <td>
                        <strong>{r.name}</strong>
                        {r.email ? (
                          <div className="muted small">{r.email}</div>
                        ) : null}
                      </td>
                      <td>
                        <span className="level-chip">L{r.level}</span>
                      </td>
                      <td>{r.practices}</td>
                      <td>
                        <strong>{r.xp}</strong>{" "}
                        <span className="muted small">XP</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DataState>
      )}
    </div>
  );
}
